import { useState } from 'react';
import { Eye, EyeOff, Lock, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { useLoginMutation } from '@/hooks/useAuth';

interface InlineLoginFormProps {
  /**
   * Wird aufgerufen, nachdem der Login (POST /auth/login) erfolgreich
   * war. Verbraucher sollte hier den Lock-Probe-Refetch triggern.
   */
  onUnlocked: () => void | Promise<void>;
  /** Optionaler Hint-Text (z.B. "selber User wie aktuelles Login"). */
  hint?: string;
}

/**
 * Kompakte Inline-Login-Form für Lock-Banner.
 *
 * Re-Authentifizierung im selben Browser-Tab ohne Logout. Wir nutzen die
 * bestehende `useLoginMutation` (POST /auth/login) - der Auth-Store
 * übernimmt den neuen Token, und der KEK-Cache im Backend ist nach diesem
 * Call wieder warm.
 *
 * LoginRequest-Schema (siehe types/api.gen.ts) enthält aktuell nur
 * `password` (Single-User-Tool, kein User-Konzept). Wir zeigen das Username-
 * Feld trotzdem als Read-only Hinweis - das macht klar, dass es um Re-Auth
 * für den eingeloggten User geht.
 */
export function InlineLoginForm({
  onUnlocked,
  hint,
}: InlineLoginFormProps): JSX.Element {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const login = useLoginMutation();

  const submit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (!password) return;
    login.mutate(
      { password },
      {
        onSuccess: () => {
          setPassword('');
          void onUnlocked();
        },
      }
    );
  };

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-3"
      data-testid="inline-login-form"
    >
      {hint && (
        <p className="inline-flex items-center gap-2 text-xs text-muted">
          <Lock className="size-3" aria-hidden="true" />
          <span>{hint}</span>
        </p>
      )}
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Input
            label="Master-Passwort"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="inline-login-password"
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          onClick={() => setShowPassword((v) => !v)}
          leftIcon={showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
        >
          {showPassword ? 'Verbergen' : 'Anzeigen'}
        </Button>
      </div>
      <div className="flex items-center justify-end gap-2">
        {login.isError && <ErrorAlert error={login.error} className="flex-1" />}
        <Button
          type="submit"
          loading={login.isPending}
          disabled={!password}
          leftIcon={<LogIn className="size-4" />}
          data-testid="inline-login-submit"
        >
          Entsperren
        </Button>
      </div>
    </form>
  );
}
